import type { IServerPubSub, ServerEventBase } from '@/services/transport/serverEvents'
import type {
  CentrifugeConnection,
  CentrifugeConnectionOnPublicationContext,
} from '@/services/transport/serverEventsSubscriptionConnectionBuilderService'
import type { Optional } from '@/types'

type EventListenerHandler<T, R> = (payload: ServerEventBase<T, R>) => void

type StatusChangeListener = (isConnected: boolean) => void

export default class ServerEventsSubscriptionService<
  TSub = Record<never, unknown>,
  TPub = Record<never, unknown>,
> implements IServerPubSub<TPub, TSub> {
  private eventListeners: Partial<Record<keyof TSub, Array<EventListenerHandler<keyof TSub, TSub[keyof TSub]>>>> = {}

  private statusChangeListeners: StatusChangeListener[] = []

  private connection: CentrifugeConnection | null = null

  private initConnectPromise: Promise<CentrifugeConnection> | null = null

  constructor(private createConnection: () => Promise<CentrifugeConnection>) {}

  public subscribe<K extends keyof TSub>(eventName: K, handler: EventListenerHandler<K, TSub[K]>) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = []
    }
    const eventTypeListeners = this.eventListeners[eventName] as Array<EventListenerHandler<K, TSub[K]>>
    eventTypeListeners.push(handler)
    this.getOrCreateConnection()
  }

  public unsubscribe<K extends keyof TSub>(eventName: K, handler: EventListenerHandler<K, TSub[K]>) {
    const eventTypeListeners = this.eventListeners[eventName] as Optional<Array<EventListenerHandler<K, TSub[K]>>>
    if (!eventTypeListeners) {
      return
    }
    const index = eventTypeListeners.indexOf(handler)
    if (index >= 0) {
      eventTypeListeners.splice(index, 1)
    }
  }

  public unsubscribeAll<T extends keyof TSub>(eventName: T) {
    if (this.eventListeners[eventName]) {
      delete this.eventListeners[eventName]
    }
  }

  public async publish<K extends keyof TPub>(eventName: K, data: TPub[K]) {
    const connection = await this.getOrCreateConnection()
    await connection.publish(eventName as string, data)
  }

  public addStatusChangeListener(handler: StatusChangeListener) {
    this.statusChangeListeners.push(handler)
    handler(this.connection?.state === 'connected')
    this.getOrCreateConnection()
  }

  public removeStatusChangeListener(handler: StatusChangeListener) {
    const index = this.statusChangeListeners.indexOf(handler)
    if (index >= 0) {
      this.statusChangeListeners.splice(index, 1)
    }
  }

  protected runEventHandlers<K extends keyof TSub>(data: ServerEventBase<K, TSub[K]>) {
    const eventHandlers = this.eventListeners[data.type]
    if (!eventHandlers) {
      return
    }
    eventHandlers.forEach((t) => t(data))
  }

  protected serverEventHandler(ctx: CentrifugeConnectionOnPublicationContext) {
    const data = ctx.data
    if (typeof data !== 'object' || data === null) {
      return
    }
    if (!('type' in data)) {
      return
    }
    if (typeof data.type !== 'string') {
      return
    }
    this.runEventHandlers(data as ServerEventBase<keyof TSub, TSub[keyof TSub]>)
  }

  private async initCentrifuge() {
    const connection = await this.createConnection()
    connection.onDisconnected(() => {
      this.statusChangeListeners.forEach((cb) => cb(false))
    })
    connection.onConnected(() => {
      this.statusChangeListeners.forEach((cb) => cb(true))
    })
    connection.onPublication((ctx) => {
      this.serverEventHandler(ctx)
    })
    return connection
  }

  private async getInitConnection() {
    if (!this.initConnectPromise) {
      this.initConnectPromise = this.initCentrifuge()
    }
    return this.initConnectPromise
  }

  public async getOrCreateConnection() {
    if (!this.connection) {
      this.connection = await this.getInitConnection()
      this.initConnectPromise = null
    }

    if (this.connection.state !== 'connected' && this.connection.state !== 'connecting') {
      this.connection.connect()
    }

    return this.connection
  }

  public async disconnectIfConnected() {
    let connection = this.connection
    if (!connection && this.initConnectPromise) {
      connection = await this.initConnectPromise
    }
    if (!connection) {
      return
    }
    connection.disconnect()
    connection.removeAllListeners()
    this.connection = null
  }
}
